/** @format */
import { TFunction } from 'i18next';

import { ProductConditionValue } from '@toucan/types/product';

export enum ProductConditionStarRating {
    ZERO = '0',
    ONE = '1',
    TWO = '2',
    THREE = '3',
    FOUR = '4',
    FIVE = '5',
    SIX = '6',
}

export enum SearchFacet {
    modelCategory = 'modelCategory',
    modelBrand = 'modelBrand',
    modelProductType = 'modelProductType',
    modelSystem = 'modelSystem',
    productPrice = 'productPrice',
    productConditionStarRating = 'productConditionStarRating',
    productPriceModifier = 'productPriceModifierIds',
    modelAaBatteryCompatableModelSpecificationBool = 'modelAaBatteryCompatableModelSpecificationBool',
    modelAutoFocusPointsModelSpecificationNum = 'modelAutoFocusPointsModelSpecificationNum',
    modelBuiltInFlashModelSpecificationBool = 'modelBuiltInFlashModelSpecificationBool',
    modelBuiltInWifiModelSpecificationBool = 'modelBuiltInWifiModelSpecificationBool',
    modelBulitInMicrophoneModelSpecificationBool = 'modelBulitInMicrophoneModelSpecificationBool',
    modelCameraMountModelSpecificationPhrase = 'modelCameraMountModelSpecificationPhrase',
    modelFilterThreadMmModelSpecificationNum = 'modelFilterThreadMmModelSpecificationNum',
    modelHeadphoneJackModelSpecificationBool = 'modelHeadphoneJackModelSpecificationBool',
    modelLensMountModelSpecificationPhrase = 'modelLensMountModelSpecificationPhrase',
    modelMaximumApertureTeleFModelSpecificationNum = 'modelMaximumApertureTeleFModelSpecificationNum',
    modelMaximumApertureWideFModelSpecificationNum = 'modelMaximumApertureWideFModelSpecificationNum',
    modelMaximumFramesPerSecondModelSpecificationNum = 'modelMaximumFramesPerSecondModelSpecificationNum',
    modelMaximumIsoModelSpecificationNum = 'modelMaximumIsoModelSpecificationNum',
    modelMaximumVideoResolutionModelSpecificationPhrase = 'modelMaximumVideoResolutionModelSpecificationPhrase',
    modelMegapixelsModelSpecificationNum = 'modelMegapixelsModelSpecificationNum',
    modelMicrophoneJackModelSpecificationPhrase = 'modelMicrophoneJackModelSpecificationPhrase',
    modelMinimumFocusDistanceMModelSpecificationNum = 'modelMinimumFocusDistanceMModelSpecificationNum',
    modelSensorFormatModelSpecificationPhrase = 'modelSensorFormatModelSpecificationPhrase',
    modelStorageMediaTypeModelSpecificationPhrase = 'modelStorageMediaTypeModelSpecificationPhrase',
    modelTouchscreenModelSpecificationBool = 'modelTouchscreenModelSpecificationBool',
    modelUsbPortTypeModelSpecificationPhrase = 'modelUsbPortTypeModelSpecificationPhrase',
    modelViewfinderTypeModelSpecificationPhrase = 'modelViewfinderTypeModelSpecificationPhrase',
    modelWeightGramsModelSpecificationNum = 'modelWeightGramsModelSpecificationNum',
    modelMinimumIsoModelSpecificationNum = 'modelMinimumIsoModelSpecificationNum',
    modelMinimumShutterSpeedXSecondsModelSpecificationNum = 'modelMinimumShutterSpeedXSecondsModelSpecificationNum',
    modelMaximumVideoFrameRateFpsModelSpecificationNum = 'modelMaximumVideoFrameRateFpsModelSpecificationNum',
    modelYearOfReleaseModelSpecificationNum = 'modelYearOfReleaseModelSpecificationNum',
    modelApertureBladesModelSpecificationNum = 'modelApertureBladesModelSpecificationNum',
    modelArticulatingLcdScreenModelSpecificationBool = 'modelArticulatingLcdScreenModelSpecificationBool',
    modelAutoFocusModelSpecificationBool = 'modelAutoFocusModelSpecificationBool',
    modelBuiltInBluetoothModelSpecificationBool = 'modelBuiltInBluetoothModelSpecificationBool',
    modelBuiltInLensHoodModelSpecificationBool = 'modelBuiltInLensHoodModelSpecificationBool',
    modelCameraTypeModelSpecificationPhrase = 'modelCameraTypeModelSpecificationPhrase',
    modelDropInFilterDiameterModelSpecificationNum = 'modelDropInFilterDiameterModelSpecificationNum',
    modelFileFormatModelSpecificationPhrase = 'modelFileFormatModelSpecificationPhrase',
    modelFlashCompatibilityModelSpecificationPhrase = 'modelFlashCompatibilityModelSpecificationPhrase',
    modelHotShoeConnectionModelSpecificationBool = 'modelHotShoeConnectionModelSpecificationBool',
    modelImageStabilizationModelSpecificationBool = 'modelImageStabilizationModelSpecificationBool',
    modelIntegratedCleaningModelSpecificationBool = 'modelIntegratedCleaningModelSpecificationBool',
    modelLcdResolutionPixelsModelSpecificationNum = 'modelLcdResolutionPixelsModelSpecificationNum',
    modelLcdSizeInchesModelSpecificationNum = 'modelLcdSizeInchesModelSpecificationNum',
    modelLiveViewModelSpecificationBool = 'modelLiveViewModelSpecificationBool',
    modelMaximumShutterSpeed1SecondsModelSpecificationPhrase = 'modelMaximumShutterSpeed1SecondsModelSpecificationPhrase',
    modelMinimumApertureFModelSpecificationNum = 'modelMinimumApertureFModelSpecificationNum',
    modelOpticalZoomXModelSpecificationNum = 'modelOpticalZoomXModelSpecificationNum',
    modelSensorTypeModelSpecificationPhrase = 'modelSensorTypeModelSpecificationPhrase',
    modelShutterRatingLifeModelSpecificationNum = 'modelShutterRatingLifeModelSpecificationNum',
    modelViewfinderCoverageModelSpecificationNum = 'modelViewfinderCoverageModelSpecificationNum',
}

export type FacetTitle = {
    [key in SearchFacet]: string;
};

export type StarRatingDisplayName = {
    [value in ProductConditionStarRating]: string;
};

export function getProductConditionStarRating(
    condition: ProductConditionValue
) {
    return {
        [ProductConditionValue.UNKNOWN]: null,
        [ProductConditionValue.FAULTY]: ProductConditionStarRating.ZERO,
        [ProductConditionValue.SR]: ProductConditionStarRating.ZERO,
        [ProductConditionValue['S/R']]: ProductConditionStarRating.ZERO,
        [ProductConditionValue.HEAVILY_USED]: ProductConditionStarRating.ONE,
        [ProductConditionValue['HEAVILY-USED']]: ProductConditionStarRating.ONE,
        [ProductConditionValue.WELL_USED]: ProductConditionStarRating.TWO,
        [ProductConditionValue['WELL-USED']]: ProductConditionStarRating.TWO,
        [ProductConditionValue.GOOD]: ProductConditionStarRating.THREE,
        [ProductConditionValue.EXCELLENT]: ProductConditionStarRating.FOUR,
        [ProductConditionValue.LIKE_NEW]: ProductConditionStarRating.FIVE,
        [ProductConditionValue['LIKE-NEW']]: ProductConditionStarRating.FIVE,
        [ProductConditionValue.NEW]: ProductConditionStarRating.SIX,
    }[condition];
}

export function getStarRatingDisplayName(t: TFunction): StarRatingDisplayName {
    const options = {
        context: 'searchFacet',
    };
    return {
        [ProductConditionStarRating.ZERO]: t('Spares and repairs', options),
        [ProductConditionStarRating.ONE]: t('Heavily used', options),
        [ProductConditionStarRating.TWO]: t('Well used', options),
        [ProductConditionStarRating.THREE]: t('Good', options),
        [ProductConditionStarRating.FOUR]: t('Excellent', options),
        [ProductConditionStarRating.FIVE]: t('Like new', options),
        [ProductConditionStarRating.SIX]: t('Brand new', options),
    };
}

export function getTranslatedFacetTitles(t: TFunction): FacetTitle {
    const options = {
        context: 'searchFacet',
    };
    return {
        [SearchFacet.modelCategory]: t('Category', options),
        [SearchFacet.modelBrand]: t('Brand', options),
        [SearchFacet.modelProductType]: t('Product type', options),
        [SearchFacet.productPrice]: t('Price', options),
        [SearchFacet.productPriceModifier]: t('Accessories', options),
        [SearchFacet.productConditionStarRating]: t(
            'Cosmetic condition',
            options
        ),
        [SearchFacet.modelSystem]: t('System', options),
        [SearchFacet.modelAaBatteryCompatableModelSpecificationBool]: t(
            'AA battery compatible',
            options
        ),
        [SearchFacet.modelAutoFocusPointsModelSpecificationNum]: t(
            'Autofocus points',
            options
        ),
        [SearchFacet.modelBuiltInFlashModelSpecificationBool]: t(
            'Built-in flash',
            options
        ),
        [SearchFacet.modelBuiltInWifiModelSpecificationBool]: t(
            'Built-in wifi',
            options
        ),
        [SearchFacet.modelBulitInMicrophoneModelSpecificationBool]: t(
            'Built-in microphone',
            options
        ),
        [SearchFacet.modelCameraMountModelSpecificationPhrase]: t(
            'Camera mount',
            options
        ),
        [SearchFacet.modelFilterThreadMmModelSpecificationNum]: t(
            'Filter thread (mm)',
            options
        ),
        [SearchFacet.modelHeadphoneJackModelSpecificationBool]: t(
            'Headphone jack',
            options
        ),
        [SearchFacet.modelLensMountModelSpecificationPhrase]: t(
            'Lens mount',
            options
        ),
        [SearchFacet.modelMaximumApertureTeleFModelSpecificationNum]: t(
            'Maximum aperture tele F',
            options
        ),
        [SearchFacet.modelMaximumApertureWideFModelSpecificationNum]: t(
            'Maximum aperture wide F',
            options
        ),
        [SearchFacet.modelMaximumFramesPerSecondModelSpecificationNum]: t(
            'Maximum frames per second',
            options
        ),
        [SearchFacet.modelMaximumIsoModelSpecificationNum]: t(
            'Maximum ISO',
            options
        ),
        [SearchFacet.modelMaximumVideoResolutionModelSpecificationPhrase]: t(
            'Maximum video resolution',
            options
        ),
        [SearchFacet.modelMegapixelsModelSpecificationNum]: t(
            'Megapixels',
            options
        ),
        [SearchFacet.modelMicrophoneJackModelSpecificationPhrase]: t(
            'Microphone jack',
            options
        ),
        [SearchFacet.modelMinimumFocusDistanceMModelSpecificationNum]: t(
            'Minimum focus distance',
            options
        ),
        [SearchFacet.modelSensorFormatModelSpecificationPhrase]: t(
            'Sensor format',
            options
        ),
        [SearchFacet.modelStorageMediaTypeModelSpecificationPhrase]: t(
            'Storage media type',
            options
        ),
        [SearchFacet.modelTouchscreenModelSpecificationBool]: t(
            'Touchscreen',
            options
        ),
        [SearchFacet.modelUsbPortTypeModelSpecificationPhrase]: t(
            'USB port type',
            options
        ),
        [SearchFacet.modelViewfinderTypeModelSpecificationPhrase]: t(
            'Viewfinder',
            options
        ),
        [SearchFacet.modelWeightGramsModelSpecificationNum]: t(
            'Weight (grams)',
            options
        ),
        [SearchFacet.modelMinimumIsoModelSpecificationNum]: t(
            'Minimum ISO',
            options
        ),
        [SearchFacet.modelMinimumShutterSpeedXSecondsModelSpecificationNum]: t(
            'Minimum shutter speed',
            options
        ),
        [SearchFacet.modelMaximumVideoFrameRateFpsModelSpecificationNum]: t(
            'Maximum video framerate',
            options
        ),
        [SearchFacet.modelYearOfReleaseModelSpecificationNum]: t(
            'Year of release',
            options
        ),
        [SearchFacet.modelApertureBladesModelSpecificationNum]: t(
            'Aperture blade count',
            options
        ),
        [SearchFacet.modelArticulatingLcdScreenModelSpecificationBool]: t(
            'Articulating LCD screen',
            options
        ),
        [SearchFacet.modelAutoFocusModelSpecificationBool]: t(
            'Auto focus',
            options
        ),
        [SearchFacet.modelBuiltInBluetoothModelSpecificationBool]: t(
            'Built-in bluetooth',
            options
        ),
        [SearchFacet.modelBuiltInLensHoodModelSpecificationBool]: t(
            'Build-in lens hood',
            options
        ),
        [SearchFacet.modelCameraTypeModelSpecificationPhrase]: t(
            'Camera type',
            options
        ),
        [SearchFacet.modelDropInFilterDiameterModelSpecificationNum]: t(
            'Drop-in filter diameter',
            options
        ),
        [SearchFacet.modelFileFormatModelSpecificationPhrase]: t(
            'File format',
            options
        ),
        [SearchFacet.modelFlashCompatibilityModelSpecificationPhrase]: t(
            'Flash compatability',
            options
        ),
        [SearchFacet.modelHotShoeConnectionModelSpecificationBool]: t(
            'Hot shoe connection',
            options
        ),
        [SearchFacet.modelImageStabilizationModelSpecificationBool]: t(
            'Image stabalization',
            options
        ),
        [SearchFacet.modelIntegratedCleaningModelSpecificationBool]: t(
            'Integrated cleaning',
            options
        ),
        [SearchFacet.modelLcdResolutionPixelsModelSpecificationNum]: t(
            'LCD resolution (pixels)',
            options
        ),
        [SearchFacet.modelLcdSizeInchesModelSpecificationNum]: t(
            'LCD size (inches)',
            options
        ),
        [SearchFacet.modelLiveViewModelSpecificationBool]: t(
            'Live view',
            options
        ),
        [SearchFacet.modelMaximumShutterSpeed1SecondsModelSpecificationPhrase]:
            t('Maximum shutter speed (seconds)', options),
        [SearchFacet.modelMinimumApertureFModelSpecificationNum]: t(
            'Minimum aperture',
            options
        ),
        [SearchFacet.modelOpticalZoomXModelSpecificationNum]: t(
            'Optical zoom',
            options
        ),
        [SearchFacet.modelSensorTypeModelSpecificationPhrase]: t(
            'Sensor type',
            options
        ),
        [SearchFacet.modelShutterRatingLifeModelSpecificationNum]: t(
            'Shutter rating',
            options
        ),
        [SearchFacet.modelViewfinderCoverageModelSpecificationNum]: t(
            'Viewfinder coverage',
            options
        ),
    };
}

export function getTranslatedHumanisedFacetTitles(
    t: TFunction
): Partial<FacetTitle> {
    return {
        ...getTranslatedFacetTitles(t),
        [SearchFacet.productConditionStarRating]: t('How it looks'),
        [SearchFacet.productPrice]: t('Your budget'),
    };
}

/**
 * Escapes double quotes in a string e.g. Messenger 15" -> Messenger 15\"
 */
export function escapeDoubleQuotes(string: string): string {
    return string.replace(/"/g, '\\"');
}
